// Enregistreur audio navigateur (MediaRecorder + getUserMedia). Sans dépendance Vue.
// Expose le vrai mimeType + un nom de fichier à extension correcte : Whisper détecte
// le format via l'extension, donc on ne hardcode JAMAIS .wav.

export interface RecordedAudio { blob: Blob; fileName: string; }

function pickMimeType(): string {
  const MR: any = (window as any).MediaRecorder
  if (!MR || !MR.isTypeSupported) return ''
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
  return candidates.find(t => MR.isTypeSupported(t)) || ''
}

function extFor(mime: string): string {
  if (mime.includes('webm')) return 'webm'
  if (mime.includes('mp4')) return 'mp4'
  if (mime.includes('ogg')) return 'ogg'
  return 'webm'
}

export default class AudioRecorder {
  static isSupported(): boolean {
    return typeof window !== 'undefined'
      && window.isSecureContext === true
      && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      && typeof (window as any).MediaRecorder !== 'undefined'
      && pickMimeType() !== '' // au moins un conteneur connu (webm/mp4/ogg) → extension fiable
  }

  private stream: MediaStream | null = null;

  private recorder: any | null = null;

  private chunks: BlobPart[] = [];

  private mimeType = '';

  async start(): Promise<void> {
    this.mimeType = pickMimeType()
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    const MR: any = (window as any).MediaRecorder
    this.recorder = this.mimeType ? new MR(this.stream, { mimeType: this.mimeType }) : new MR(this.stream)
    // Refléter le conteneur réellement choisi par le navigateur → nom de fichier à extension correcte.
    if (this.recorder.mimeType) this.mimeType = this.recorder.mimeType
    this.chunks = []
    this.recorder.ondataavailable = (e: any) => { if (e.data && e.data.size > 0) this.chunks.push(e.data) }
    this.recorder.start()
  }

  stop(): Promise<RecordedAudio> {
    return new Promise((resolve, reject) => {
      if (!this.recorder) { reject(new Error('not-recording')); return }
      let settled = false
      // Garde-fou : si onstop ne se déclenche jamais (erreur silencieuse), on évite un spinner bloqué à l'infini.
      const timeout = window.setTimeout(() => {
        if (settled) return
        settled = true
        this.releaseStream()
        reject(new Error('stop-timeout'))
      }, 10000)
      this.recorder.onstop = () => {
        if (settled) return
        settled = true
        window.clearTimeout(timeout)
        const type = this.mimeType || 'audio/webm'
        const blob = new Blob(this.chunks, { type })
        this.releaseStream()
        resolve({ blob, fileName: `dictee.${extFor(type)}` })
      }
      try { this.recorder.stop() } catch (e) { window.clearTimeout(timeout); this.releaseStream(); reject(e) }
    })
  }

  private releaseStream(): void {
    // Libère le micro (sinon le voyant du navigateur reste allumé).
    if (this.stream) { this.stream.getTracks().forEach(t => t.stop()); this.stream = null }
    this.recorder = null
  }

  dispose(): void {
    try { if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop() } catch { /* noop */ }
    this.releaseStream()
    this.chunks = []
  }
}
